import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/form-elements/switch/switch";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import { NotificationService } from "ui-components/notification";

import { styles } from "./create-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";
import { WowFormController } from "ui-common/form/wow-form-controller";
import type { CreateLiveEventParams } from "api-contracts/rpc/create-live-event";
import { t, translate } from "../../translations/i18n";
import { createLiveEventValidators, MAX_PRICE_LIMIT } from "ui-common/form/validators/live-event-validators";
import type { LiveEventFormData } from "ui-common/form/validators/live-event-validators";

@customElement("create-live-event-modal")
export class CreateLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private showDescription = false;

  @state()
  private showMaxAttendees = false;

  @state()
  private showWhatsApp = false;

  @state()
  private showPostPurchaseMedia = false;

  @state()
  private showCtaText = false;

  @state()
  private showDuration = false;

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private isSubmitting = false;

  @state()
  private errors: Record<string, string> = {};

  @state()
  private showLinkedLinks = false;

  @state()
  private linkedLinkIds: string[] = [];

  @state()
  private availableLinks: Array<{
    id: string;
    title: string;
    has_discount: boolean;
    code: string | null;
  }> = [];

  @state()
  private isLoadingLinks = false;

  private formController!: WowFormController<LiveEventFormData>;
  private hasInitializedContentTier = false;

  override connectedCallback(): void {
    super.connectedCallback();
    this.initializeFormController();
    this.loadAvailableLinks();
  }

  private async loadAvailableLinks(): Promise<void> {
    try {
      this.isLoadingLinks = true;
      const result = await rpc("getUnifiedLinks", {});
      if (result && result.links) {
        this.availableLinks = result.links
          .filter((link: { status?: "live" | "upcoming" | "expired" }) => link.status !== "expired")
          .map((link: { id: string; title: string; has_discount: boolean; code: string | null }) => ({
            id: link.id,
            title: link.title,
            has_discount: link.has_discount,
            code: link.code,
          }));
      }
    } catch (error) {
      console.error("Error loading available links:", error);
    } finally {
      this.isLoadingLinks = false;
    }
  }

  private handleLinkToggle(linkId: string, checked: boolean): void {
    if (checked) {
      this.linkedLinkIds = [...this.linkedLinkIds, linkId];
    } else {
      this.linkedLinkIds = this.linkedLinkIds.filter((id) => id !== linkId);
    }
  }

  private initializeFormController(): void {
    const validators = createLiveEventValidators(translate as (key: string) => string);

    this.formController = new WowFormController<LiveEventFormData>(
      this,
      {
        title: "",
        description: "",
        feeEuros: "",
        scheduledDate: "",
        scheduledTime: "",
        maxAttendees: "",
        whatsappUrl: "",
        ctaText: "",
        durationHours: "",
        durationMinutes: "",
      },
      {
        validateOnBlur: true,
        validators: {
          title: [validators.validateTitle],
          feeEuros: [validators.createFeeValidator(() => this.contentTier)],
          scheduledDate: [validators.validateDate],
          scheduledTime: [validators.validateTime],
          maxAttendees: [validators.validateMaxAttendees],
        },
      },
      async (event) => {
        if (!event.isValid || !event.data) return;
        await this.submitForm(event.data);
      },
      "#live-event-form",
    );
  }

  protected override updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("featureFlags") && !this.hasInitializedContentTier && this.featureFlags.length > 0) {
      this.contentTier = this.defaultContentTier;
      this.hasInitializedContentTier = true;
    }
  }

  private async submitForm(data: LiveEventFormData): Promise<void> {
    const scheduledDate = data.scheduledDate;
    const scheduledTime = data.scheduledTime;

    const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);

    if (isNaN(scheduledDateTime.getTime())) {
      NotificationService.error("Invalid date or time format");
      return;
    }

    if (scheduledDateTime <= new Date()) {
      this.errors = { datetime: translate("createLiveEventsModal.dateMustBeFuture") };
      return;
    }

    this.isSubmitting = true;

    try {
      const localDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const params: CreateLiveEventParams = {
        title: data.title.trim(),
        description: data.description?.trim() || undefined,
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" ? Math.round(parseFloat(data.feeEuros) * 100) : undefined,
        scheduled_start_time: isoString,
        max_attendees: data.maxAttendees ? Number(data.maxAttendees) : undefined,
        thumbnail_file_id: this.thumbnailFileId,
        whatsapp_url: data.whatsappUrl?.trim() || undefined,
        post_buying_video_file_id: this.postBuyingVideoFileId,
        cta_text: data.ctaText?.trim() || undefined,
        expected_duration: this.calculateDurationMinutes(data.durationHours, data.durationMinutes),
        link_ids: this.showLinkedLinks && this.linkedLinkIds.length > 0 ? this.linkedLinkIds : undefined,
      };

      const result = await rpc("create-live-event", params);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success("Live event created successfully!");

        this.dispatchEvent(
          new CustomEvent("event-created", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay to allow notification to be seen
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || "Failed to create live event"
            : "Failed to create live event";
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error("Failed to create live event");
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleSubmit(): void {
    this.formController.submit();
  }

  private handleClose() {
    // Reset form when closing
    this.resetForm();
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private get defaultContentTier(): "free" | "premium" {
    if (this.featureFlags.includes("paid-live-events")) {
      return "premium";
    }
    return "free";
  }

  private get hasPaidLiveEvents(): boolean {
    return this.featureFlags.includes("paid-live-events");
  }

  private get hasOpenLiveEvents(): boolean {
    return this.featureFlags.includes("open-live-events");
  }

  private get accessLevelOptions(): Array<{ value: string; label: string }> {
    const options: Array<{ value: string; label: string }> = [];

    if (this.hasOpenLiveEvents) {
      options.push({ value: "free", label: translate("createLiveEventsModal.accessFree") });
    }

    if (this.hasPaidLiveEvents) {
      options.push({ value: "premium", label: translate("createLiveEventsModal.accessPaid") });
    }

    return options;
  }

  private get todayDate(): string {
    return new Date().toISOString().split("T")[0]!;
  }

  private calculateDurationMinutes(hours: string, minutes: string): number | undefined {
    if (!hours && !minutes) return undefined;
    const hoursNum = hours ? Number(hours) : 0;
    const minutesNum = minutes ? Number(minutes) : 0;
    const totalMinutes = hoursNum * 60 + minutesNum;
    return totalMinutes > 0 ? totalMinutes : undefined;
  }

  private resetForm() {
    this.formController.reset();
    this.contentTier = this.defaultContentTier;
    this.thumbnailFileId = undefined;
    this.thumbnailUrl = undefined;
    this.showDescription = false;
    this.showMaxAttendees = false;
    this.showWhatsApp = false;
    this.showPostPurchaseMedia = false;
    this.showCtaText = false;
    this.showDuration = false;
    this.showLinkedLinks = false;
    this.linkedLinkIds = [];
    this.postBuyingVideoUrl = "";
    this.postBuyingVideoFileId = undefined;
    this.isSubmitting = false;
    this.errors = {};
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  protected override render() {
    const submitText = this.isSubmitting
      ? t("createLiveEventsModal.creating")
      : t("createLiveEventsModal.submitButton");

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">${t("createLiveEventsModal.header")}</span>

        <form id="live-event-form" class="form-content">
          <!-- Event Cover Image -->
          <media-uploader
            responsive
            context="cover_image"
            allowedMediaType="image"
            label="${t("createLiveEventsModal.firstThumb")}"
            subtitle="${t("createLiveEventsModal.firstThumbSubtitle")}"
            viewport="cover"
            currentFileUrl=${this.thumbnailUrl}
            @change=${this.handleThumbnailUpload}
          ></media-uploader>

          <!-- Title -->
          <wow-input
            name=${this.formController.fieldNames.title}
            label="${t("createLiveEventsModal.title")}"
            placeholder="${t("createLiveEventsModal.titlePlaceholder")}"
          ></wow-input>

          <!-- Date and Time Section -->
          <div class="date-time-section">
            <div class="section-header">
              <span class="section-label">${t("createLiveEventsModal.dateTimeLabel")}</span>
              <span class="section-subtitle">${t("createLiveEventsModal.dateTimeSubtitle")}</span>
            </div>
            <div class="form-row">
              <wow-input
                name=${this.formController.fieldNames.scheduledDate}
                type="date"
                placeholder="${t("createLiveEventsModal.date")}"
                min=${this.todayDate}
                @input=${() => {
                  this.errors = { ...this.errors, datetime: "" };
                }}
              ></wow-input>

              <wow-input
                name=${this.formController.fieldNames.scheduledTime}
                type="time"
                placeholder="${t("createLiveEventsModal.time")}"
                @input=${() => {
                  this.errors = { ...this.errors, datetime: "" };
                }}
              ></wow-input>
            </div>
          </div>

          <!-- Expected Duration Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.setDuration")}"
              ?checked=${this.showDuration}
              @change=${(e: CustomEvent) => {
                this.showDuration = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showDuration
              ? html`
                  <div class="form-row">
                    <wow-input
                      name=${this.formController.fieldNames.durationHours}
                      type="number"
                      inputmode="numeric"
                      placeholder="${t("createLiveEventsModal.durationHours")}"
                      min="0"
                      max="23"
                      step="1"
                      @keydown=${(e: KeyboardEvent) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight" &&
                          e.key !== "Tab"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    ></wow-input>
                    <wow-input
                      name=${this.formController.fieldNames.durationMinutes}
                      type="number"
                      inputmode="numeric"
                      placeholder="${t("createLiveEventsModal.durationMinutes")}"
                      min="0"
                      max="59"
                      step="1"
                      @keydown=${(e: KeyboardEvent) => {
                        if (
                          !/[0-9]/.test(e.key) &&
                          e.key !== "Backspace" &&
                          e.key !== "Delete" &&
                          e.key !== "ArrowLeft" &&
                          e.key !== "ArrowRight" &&
                          e.key !== "Tab"
                        ) {
                          e.preventDefault();
                        }
                      }}
                    ></wow-input>
                  </div>
                `
              : nothing}
          </div>

          <!-- Access Level -->
          <wow-select
            name="access-level"
            label="${t("createLiveEventsModal.accessLevel")}"
            .options=${this.accessLevelOptions}
            .value=${this.contentTier}
            ?disabled=${this.accessLevelOptions.length <= 1}
            @change=${(e: CustomEvent) => {
              this.contentTier = e.detail.value;
              this.errors = { ...this.errors, fee: "" };
            }}
          ></wow-select>

          <!-- Ticket Price - only show for premium events -->
          ${this.contentTier === "premium"
            ? html`
                <wow-input
                  name=${this.formController.fieldNames.feeEuros}
                  type="number"
                  inputmode="decimal"
                  label="${t("createLiveEventsModal.price")}"
                  placeholder="${t("createLiveEventsModal.pricePlaceholder")}"
                  min="0.50"
                  max="${MAX_PRICE_LIMIT}"
                  step="0.01"
                ></wow-input>
              `
            : nothing}

          <!-- Link Discounts/Links Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.linkDiscounts")}"
              ?checked=${this.showLinkedLinks}
              @change=${(e: CustomEvent) => {
                this.showLinkedLinks = e.detail.checked;
                if (!e.detail.checked) {
                  this.linkedLinkIds = [];
                }
              }}
            ></wow-switch>
            ${this.showLinkedLinks
              ? html`
                  <div class="linked-links-section">
                    ${this.isLoadingLinks
                      ? html`<div class="loading-links">${t("createLiveEventsModal.loadingLinks")}</div>`
                      : this.availableLinks.length === 0
                        ? html`<div class="no-links">${t("createLiveEventsModal.noLinksAvailable")}</div>`
                        : html`
                            <div class="links-checklist">
                              ${this.availableLinks.map(
                                (link) => html`
                                  <label class="link-checkbox-item">
                                    <input
                                      type="checkbox"
                                      .checked=${this.linkedLinkIds.includes(link.id)}
                                      @change=${(e: Event) =>
                                        this.handleLinkToggle(link.id, (e.target as HTMLInputElement).checked)}
                                    />
                                    <span class="link-label">
                                      ${link.title}
                                      ${link.has_discount
                                        ? html`<span class="discount-badge"
                                            >${link.code || t("createLiveEventsModal.discount")}</span
                                          >`
                                        : nothing}
                                    </span>
                                  </label>
                                `,
                              )}
                            </div>
                          `}
                  </div>
                `
              : nothing}
          </div>

          <!-- Add Description Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.addDescription")}"
              ?checked=${this.showDescription}
              @change=${(e: CustomEvent) => {
                this.showDescription = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showDescription
              ? html`
                  <wow-textarea
                    name=${this.formController.fieldNames.description}
                    placeholder="${t("createLiveEventsModal.descriptionPlaceholder")}"
                    rows="3"
                    maxlength="750"
                    autogrow
                  ></wow-textarea>
                `
              : nothing}
          </div>

          <!-- Limit Number of Participants Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.limitParticipants")}"
              ?checked=${this.showMaxAttendees}
              @change=${(e: CustomEvent) => {
                this.showMaxAttendees = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showMaxAttendees
              ? html`
                  <wow-input
                    name=${this.formController.fieldNames.maxAttendees}
                    type="number"
                    inputmode="numeric"
                    placeholder="${t("createLiveEventsModal.maxAttendeesPlaceholder")}"
                    step="1"
                    @keydown=${(e: KeyboardEvent) => {
                      if (
                        !/[0-9]/.test(e.key) &&
                        e.key !== "Backspace" &&
                        e.key !== "Delete" &&
                        e.key !== "ArrowLeft" &&
                        e.key !== "ArrowRight" &&
                        e.key !== "Tab"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    @input=${() => this.formController.validateField("maxAttendees")}
                  ></wow-input>
                `
              : nothing}
          </div>

          <!-- WhatsApp Community Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.whatsAppCommunity")}"
              ?checked=${this.showWhatsApp}
              @change=${(e: CustomEvent) => {
                this.showWhatsApp = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showWhatsApp
              ? html`
                  <wow-input
                    name=${this.formController.fieldNames.whatsappUrl}
                    type="url"
                    placeholder="${t("createLiveEventsModal.whatsAppPlaceholder")}"
                  ></wow-input>
                `
              : nothing}
          </div>

          <!-- Post-Purchase Media Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.addPostPurchaseMedia")}"
              ?checked=${this.showPostPurchaseMedia}
              @change=${(e: CustomEvent) => {
                this.showPostPurchaseMedia = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showPostPurchaseMedia
              ? html`
                  <media-uploader
                    responsive
                    context="cover_image"
                    label="${t("createLiveEventsModal.secondThumb")}"
                    subtitle=${t("createLiveEventsModal.secondThumbSubtitle")}
                    viewport="post"
                    currentFileUrl=${this.postBuyingVideoUrl}
                    @change=${this.handleVideoUpload}
                  ></media-uploader>
                `
              : nothing}
          </div>

          <!-- CTA Text Toggle -->
          <div class="toggle-field">
            <wow-switch
              label="${t("createLiveEventsModal.addCtaText")}"
              ?checked=${this.showCtaText}
              @change=${(e: CustomEvent) => {
                this.showCtaText = e.detail.checked;
              }}
            ></wow-switch>
            ${this.showCtaText
              ? html`
                  <wow-input
                    name=${this.formController.fieldNames.ctaText}
                    placeholder="${t("createLiveEventsModal.ctaButtonPlaceholder")}"
                    maxlength="255"
                  ></wow-input>
                `
              : nothing}
          </div>
        </form>

        <span slot="footer-button-text">${submitText}</span>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "create-live-event-modal": CreateLiveEventModal;
  }
}
